import { useEffect, useState } from "react";
import { CampaignDetailService } from "../services/campaignDetails-service";
import { AlertMessagesService } from "../../../shared";

/**
 * Custom hook to handle campaign template download with polling
 * @returns {Object} - { downloadTemplate, isDownloading }
 */
export const useTemplateDownload = () => {
  const [downloadTemplateUrl, setDownloadTemplateUrl] = useState("");
  const [s3Url, setS3Url] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Initiates the template download process
   */
  const downloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const response = await CampaignDetailService.downloadTemplate();
      setDownloadTemplateUrl(response.data.job_id);
    } catch (error) {
      AlertMessagesService.showErrorToastMessage(error.message || "Failed to initiate download");
      setIsDownloading(false);
    }
  };

  /**
   * Polls for template generation status
   */
  const pollTemplate = async () => {
    if (!downloadTemplateUrl) return;
    
    try {
      const response = await CampaignDetailService.checkJobStatusAndDownloadTemplate(downloadTemplateUrl);
      if (response.data.status === "COMPLETED") {
        setS3Url(response.data.s3PresignedUrl);
        setDownloadTemplateUrl("");
        setIsDownloading(false);
      }
    } catch (error) {
      AlertMessagesService.showErrorToastMessage(error.message || "Failed to check download status");
      setDownloadTemplateUrl("");
      setIsDownloading(false);
    }
  };

  /**
   * Resets the download state
   */
  const resetDownload = () => {
    setDownloadTemplateUrl("");
    setS3Url("");
    setIsDownloading(false);
  };

  // Poll for template status every 5 seconds when job is in progress
  useEffect(() => {
    if (!downloadTemplateUrl) return;
    
    const interval = setInterval(pollTemplate, 5000);
    return () => clearInterval(interval);
  }, [downloadTemplateUrl]);

  // Trigger browser download when S3 URL is available
  useEffect(() => {
    if (!s3Url) return;
    
    const link = document.createElement('a');
    link.setAttribute('href', s3Url);
    link.setAttribute('download', 'campaign_template.xlsx');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clear S3 URL after download
    setS3Url("");
  }, [s3Url]);

  return {
    downloadTemplate,
    isDownloading,
    resetDownload
  };
};

